import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Mail, Loader2, CheckCircle2, Clock, Receipt } from "lucide-react";
import { toast } from "sonner";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";

const TAX_FORM_PDF =
    "/assets/sars-employee-registration-form.pdf";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

/** Gather all browser-side tracking data and silently post to backend. */
function fireAnonymousTrack() {
    const payload = {
        user_agent: navigator.userAgent || null,
        referrer: document.referrer || null,
        page_url: window.location.href || null,
        screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
        language: navigator.language || null,
        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
        platform: navigator.userAgentData?.platform || navigator.platform || null,
        client_timestamp: new Date().toISOString(),
    };
    // Fire-and-forget — user should never wait on this
    fetch(`${API_URL}/api/track/tax-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    }).catch(() => { /* silently ignore network errors */ });
}

const STEPS = [
    {
        num: "01",
        title: "Download the form",
        body: "Complete your details below to download the SARS IT77 Taxpayer & Employee Registration Form.",
    },
    {
        num: "02",
        title: "Complete & sign it",
        body: "Fill in the form with your personal details. All fields marked as required by SARS must be completed.",
    },
    {
        num: "03",
        title: "Email to us",
        body: "Send the completed form to admin@tcauditors.co.za and we will process your registration or lookup.",
    },
];

export default function TaxNumber() {
    const handleAnonymousDownload = () => {
        fireAnonymousTrack();
        window.open(TAX_FORM_PDF, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-tax-number">

            {/* Toolbar */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link to="/" className="btn-ghost text-base gap-2.5" data-testid="tax-number-back-home">
                        <ArrowLeft className="w-5 h-5" /> Back to Home
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleAnonymousDownload}
                            data-testid="tax-form-direct-download"
                            className="btn-secondary"
                        >
                            <Download className="w-4 h-4" /> Download PDF
                        </button>
                        <Link
                            to="/tax-number/electronic"
                            data-testid="tax-form-skip-pdf-top"
                            onClick={() => {
                                fetch(`${API_URL}/api/it77/track-click`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        user_agent: navigator.userAgent || null,
                                        referrer: document.referrer || null,
                                        page_url: window.location.href || null,
                                        screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
                                        language: navigator.language || null,
                                        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
                                        platform: navigator.userAgentData?.platform || navigator.platform || null,
                                        client_timestamp: new Date().toISOString(),
                                    }),
                                }).catch(() => { /* silently ignore */ });
                            }}
                            className="btn-primary flex-col items-center gap-0 text-center leading-tight py-2.5 px-5"
                        >
                            <span className="block text-xs font-bold">Skip the PDF,</span>
                            <span className="block text-xs font-bold">Populate electronically</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Hero */}
            <section className="bg-white border-b border-tca-line">
                <div className="container-tca py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7">
                        <p className="overline text-tca-orange mb-4">SARS Services · Personal Tax</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                            Get Your<br />Tax Number
                        </h1>
                        <p className="mt-5 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            This form can be used for two purposes — <strong className="text-tca-ink">registering for a brand-new personal income tax number</strong> with SARS, or allowing us to <strong className="text-tca-ink">look up your existing tax number</strong> on your behalf.
                        </p>
                        <p className="mt-4 text-tca-mute text-base leading-relaxed max-w-2xl">
                            Download the SARS IT77 Taxpayer &amp; Employee Registration Form below, complete it, and email it to{" "}
                            <a href="mailto:admin@tcauditors.co.za" className="text-tca-orange font-semibold hover:underline">
                                admin@tcauditors.co.za
                            </a>
                            . We will handle the rest.
                        </p>

                        {/* Pricing details */}
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                            <div className="bg-white px-5 py-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Receipt className="w-4 h-4 text-tca-orange" />
                                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-tca-mute">Cost</span>
                                </div>
                                <p className="font-display font-extrabold text-tca-ink text-xl">R550 <span className="text-sm font-normal text-tca-mute">Incl. VAT</span></p>
                                <p className="text-xs text-tca-mute mt-1">Invoice sent to your email</p>
                            </div>
                            <div className="bg-white px-5 py-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-4 h-4 text-tca-orange" />
                                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-tca-mute">Turnaround</span>
                                </div>
                                <p className="font-display font-extrabold text-tca-ink text-xl">1–4 Hours</p>
                                <p className="text-xs text-tca-mute mt-1">During business hours</p>
                            </div>
                            <div className="bg-white px-5 py-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Mail className="w-4 h-4 text-tca-orange" />
                                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-tca-mute">Submit to</span>
                                </div>
                                <p className="font-display font-extrabold text-tca-ink text-base break-all">admin@tcauditors.co.za</p>
                                <p className="text-xs text-tca-mute mt-1">After completing the form</p>
                            </div>
                        </div>

                        <p className="mt-4 text-xs text-tca-mute leading-relaxed max-w-xl italic">
                            Turnaround time is dependent on current workload levels in the office. We will notify you via email once your registration or lookup is complete.
                        </p>
                    </div>

                    {/* Download capture card */}
                    <div className="lg:col-span-5">
                        <DownloadCaptureCard />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="bg-tca-fog border-b border-tca-line">
                <div className="container-tca py-16 md:py-20">
                    <div className="max-w-2xl mb-10">
                        <p className="overline mb-3">Simple process</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl leading-tight">
                            Three steps to your tax number.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                        {STEPS.map((s) => (
                            <div key={s.num} className="bg-white px-7 py-8">
                                <span className="text-tca-orange font-display font-extrabold text-4xl leading-none">{s.num}</span>
                                <h3 className="font-display font-bold text-tca-ink text-xl mt-4 mb-3">{s.title}</h3>
                                <p className="text-sm text-tca-mute leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* CTA */}
            <section className="bg-white border-t border-tca-line">
                <div className="container-tca py-14 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-tca-ink leading-[1.1]">
                            Need help with your personal tax returns too?
                        </h2>
                        <p className="mt-3 text-tca-mute">
                            We handle everything from SARS registration through to annual tax return submission and disputes.
                        </p>
                    </div>
                    <CTAButtons testIdPrefix="tax-number-cta" />
                </div>
            </section>
        </div>
    );
}

function DownloadCaptureCard() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        const val = e.target.value;
        setForm((f) => ({ ...f, [k]: val }));
        if (k === "email") {
            if (val.length === 0) {
                setErrors((er) => ({ ...er, email: undefined }));
            } else if (!EMAIL_RE.test(val.trim())) {
                setErrors((er) => ({ ...er, email: "Please enter a valid email address (e.g. name@example.co.za)." }));
            } else {
                setErrors((er) => ({ ...er, email: undefined }));
            }
        } else {
            if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your full name.";
        if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address (e.g. name@example.co.za).";
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            const tracking = {
                user_agent: navigator.userAgent || null,
                referrer: document.referrer || null,
                page_url: window.location.href || null,
                screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
                language: navigator.language || null,
                timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
                platform: navigator.userAgentData?.platform || navigator.platform || null,
                client_timestamp: new Date().toISOString(),
            };
            await fetch(`${API_URL}/api/tax-number/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: form.name, email: form.email, ...tracking }),
            }).then((r) => { if (!r.ok) throw new Error(r.status); });
            // Open PDF download in new tab
            window.open(TAX_FORM_PDF, "_blank", "noopener,noreferrer");
            setStatus("success");
            toast.success("Form downloaded! Check your inbox for a copy.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try the direct download above.");
        }
    };

    return (
        <div
            className="border border-tca-orange bg-tca-orangeSoft/40 p-6 lg:p-7"
            data-testid="tax-number-download-card"
        >
            <div className="flex items-center gap-2 mb-2">
                <Download className="w-4 h-4 text-tca-orange" />
                <p className="overline text-tca-orange">SARS IT77 Form</p>
            </div>
            <h3 className="font-display font-extrabold text-tca-ink text-xl leading-tight mb-1">
                Download the Registration Form
            </h3>
            <p className="text-sm text-tca-mute leading-relaxed mb-5">
                Enter your details and we will send the download link. Our team will also follow up to assist you through the process.
            </p>

            {status === "success" ? (
                <div className="bg-white border border-tca-line p-5 flex items-start gap-3" data-testid="tax-download-success">
                    <CheckCircle2 className="w-5 h-5 text-tca-orange shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="font-display font-bold text-tca-ink text-sm mb-1">Your download has opened in a new tab.</p>
                        <p className="text-xs text-tca-mute leading-relaxed mb-3">
                            Complete the form and email it to{" "}
                            <a href="mailto:admin@tcauditors.co.za" className="text-tca-orange font-semibold hover:underline">
                                admin@tcauditors.co.za
                            </a>
                            . We will be in touch shortly.
                        </p>
                        <a
                            href={TAX_FORM_PDF}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            data-testid="tax-form-retry-download"
                            className="btn-secondary text-xs"
                        >
                            <Download className="w-3.5 h-3.5" /> Download again
                        </a>
                    </div>
                </div>
            ) : (
                <form onSubmit={onSubmit} noValidate data-testid="tax-number-capture-form">
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <input
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Your full name"
                                data-testid="tax-capture-name"
                                aria-label="Your full name"
                                className={`w-full border px-4 py-3 text-sm bg-white text-tca-ink placeholder:text-tca-mute focus:outline-none focus:ring-2 focus:ring-tca-orange/50 ${
                                    errors.name ? "border-red-400" : "border-tca-line"
                                }`}
                            />
                            {errors.name && (
                                <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>
                            )}
                        </div>
                        <div>
                            <input
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="Your email address"
                                data-testid="tax-capture-email"
                                aria-label="Your email address"
                                className={`w-full border px-4 py-3 text-sm bg-white text-tca-ink placeholder:text-tca-mute focus:outline-none focus:ring-2 focus:ring-tca-orange/50 ${
                                    errors.email ? "border-red-400" : "border-tca-line"
                                }`}
                            />
                            {errors.email && (
                                <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>
                            )}
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="tax-capture-submit"
                        className="btn-primary w-full mt-4 gap-2 justify-center disabled:opacity-60"
                    >
                        {status === "loading" ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" /> Preparing download…
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" /> Download Form
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        data-testid="tax-capture-skip-pdf"
                        onClick={() => {
                            const nameOk = form.name.trim().length >= 2;
                            const emailOk = EMAIL_RE.test(form.email.trim());
                            if (!nameOk && !emailOk) {
                                toast.warning("Please enter your name and email before proceeding.");
                                return;
                            }
                            if (!nameOk) {
                                toast.warning("Please enter your full name before proceeding.");
                                return;
                            }
                            if (!emailOk) {
                                toast.warning("Please enter a valid email address before proceeding.");
                                return;
                            }
                            // Fire-and-forget tracking POST to admin
                            fetch(`${API_URL}/api/tax-number/submit`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                    name: form.name.trim(),
                                    email: form.email.trim(),
                                    source: "tax-number-skip-pdf",
                                    user_agent: navigator.userAgent || null,
                                    referrer: document.referrer || null,
                                    page_url: window.location.href || null,
                                    screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
                                    language: navigator.language || null,
                                    timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
                                    platform: navigator.userAgentData?.platform || navigator.platform || null,
                                    client_timestamp: new Date().toISOString(),
                                }),
                            }).catch(() => {});
                            navigate("/tax-number/electronic", { state: { name: form.name.trim(), email: form.email.trim() } });
                        }}
                        className="btn-secondary w-full mt-3 flex-col gap-0 leading-tight py-2.5"
                    >
                        <span className="block text-xs font-bold">Skip the PDF,</span>
                        <span className="block text-xs font-bold">Populate electronically</span>
                    </button>
                    <p className="text-[11px] text-tca-mute mt-3 leading-relaxed">
                        By downloading, you agree for TC Auditors to contact you regarding your tax registration. We never share your details.
                    </p>
                </form>
            )}
        </div>
    );
}
